import styled from 'styled-components';
import { AliasToken } from 'antd/es/theme/interface';

interface ITableProps {
  drawTable: boolean;
  token: AliasToken & { tableBorderColor?: string };
  scheme?: string;
  isEmpty?: boolean;
}

export const TopScrollbar = styled('div')`
  height: 17px;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  position: sticky;
  top: 0;
  z-index: 100;
  background: white;
  border-bottom: 1px solid #d9d9d9;
`;

export const ScrollContent = styled('div')`
  height: 1px;
  width: 100%;
`;

export const BottomScrollbar = styled('div')`
  height: 17px;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  background: white;
  border-top: 1px solid #d9d9d9;
  position: sticky;
  bottom: 0;
  z-index: 100;
`;

export const ScrollNavigation = styled('div')`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const VerticalButtons = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const HorizontalButtons = styled('div')`
  display: flex;
  gap: 4px;
`;

export const TableWrapper = styled('div')<ITableProps>`
  margin-left: 20px;

  .ant-table-row-expand-icon-cell {
    display: none;
  }

  .ant-pagination {
    margin: 0px 16px 16px 16px !important;
  }

  .ant-table-row-level-0 {
    > .ant-table-cell {
      padding: 10px 0px 0px > div {
        padding: 16px;
      }
    }
  }

  .ant-table-filter-column {
    flex-direction: row-reverse;

    .ant-dropdown-trigger {
      margin-inline: 0 5px;
      color: ${(props) => {
        const color = props.token.colorPrimary;
        const isLilacTheme = color?.toLowerCase() === '#9a97d6';
        return isLilacTheme
          ? props.token.colorPrimaryText || '#000000'
          : 'inherit';
      }};

      svg {
        color: ${(props) => {
          const color = props.token.colorPrimary;
          const isLilacTheme = color?.toLowerCase() === '#9a97d6';
          return isLilacTheme
            ? props.token.colorPrimaryText || '#000000'
            : 'inherit';
        }};
      }
    }
  }

  .ant-table-wrapper {
    .ant-table {
      margin: unset !important;
    }
  }

  .ant-spin-nested-loading {
    overflow-y: initial !important;
  }

  .ant-table-row {
    .ant-table-cell:nth-of-type(5) {
      .TextColumn-root {
        background-color: transparent;
      }
    }
  }

  .ant-table-content {
    overflow-x: hidden !important;
    overflow-y: hidden !important;
  }

  .ant-table-content {
    overflow-x: hidden !important;
    overflow-y: hidden !important;
    min-height: ${(props) => (props.isEmpty ? '50vh' : 'unset')};
  }

  .ant-table-placeholder {
    display: none;
  }

  .ant-table-wrapper .ant-table {
    max-width: 100%;
    background: none !important;

    &:hover {
      background: none !important;
    }

    .ant-spin-nested-loading {
      overflow-y: scroll;
    }

    .ant-table-tbody > tr > td {
      border-bottom: ${(props) =>
        `1px solid ${props.token?.tableBorderColor || '#d9d9d9'} !important`};
      color: ${(props) => {
        if (
          props.scheme === 'darkUITheme' ||
          props.scheme === 'deepNavyTheme'
        ) {
          return '#ffffff !important';
        } else if (props.scheme === 'turquoiseTheme') {
          return '#000000 !important';
        }
        return 'inherit';
      }};
    }

    /* Remove borders from expanded rows and adjacent rows to prevent double lines */
    .ant-table-expanded-row > td {
      border: none !important;
      padding: 0 !important;
    }

    /* Remove border from row immediately before expanded row */
    .ant-table-tbody > tr[data-row-key]:has(+ .ant-table-expanded-row) > td {
      border-bottom: none !important;
    }

    /* Fallback for browsers without :has() support */
    .ant-table-tbody > tr[data-row-key] + .ant-table-expanded-row {
      margin-top: -1px;
    }

    .ant-table-tbody > tr > td.date-cell-border {
      border-right: 1px solid #d9d9d9 !important;
    }

    .ant-table-tbody > tr {
      &:hover > td:not([rowspan]) {
        background: ${(props) => props.token.colorFillContent} !important;
      }
    }

    tbody {
      vertical-align: top;

      > tr {
        background: ${(props) => props.token.colorBgLayout};
        transition: background-color 0.2s ease;

        &:hover > td:not([rowspan]) {
          background: ${(props) => props.token.colorFillContent};
        }
      }
    }

    .ant-table-expanded-row.ant-table-expanded-row-level-1 {
      .ant-table-cell {
        padding: unset;
      }
    }

    .ant-table-thead {
      tr {
        &:first-of-type > *:first-of-type,
        &:last-child > *:last-child {
          border-start-start-radius: unset !important;
          border-start-end-radius: unset !important;
        }

        th {
          background: ${(props) => props.token.colorFillQuaternary};
          font-size: ${(props) => `${props.token.fontSizeSM}px`};
          font-weight: ${(props) => props.token.fontWeightStrong};
          text-transform: capitalize;
          color: ${(props) => props.token.colorPrimaryText || 'inherit'};

          /* Force theme text color with higher specificity when colorPrimaryText is available */
          ${(props) => {
            if (props.token.colorPrimaryText) {
              return `
                &, & *, & .ant-table-column-title, & span {
                  color: ${props.token.colorPrimaryText} !important;
                }
              `;
            }
            return '';
          }}

          /* Add theme-specific vertical separators between column headers */
          &:not(:last-child)::after {
            content: '';
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
            height: 1.6em;
            width: 1px;
            background-color: ${(props) => {
              const { themesList } = require('constants/themesList');
              const currentScheme =
                props.scheme || localStorage.getItem('theme') || 'mainTheme';
              const currentTheme =
                themesList[currentScheme as keyof typeof themesList];
              return currentTheme?.colorPrimary || '#d9d9d9';
            }};
          }

          /* Ensure relative positioning for the pseudo-element */
          position: relative;
        }
      }

      tr:first-child > th[colspan] {
        padding-top: 12px !important;
      }

      tr:last-child > th {
        padding-top: 10px !important;
      }
    }
  }
`;
